import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  PlusIcon,
  TagIcon,
  FolderIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Task, TaskFeedback, TaskFinishDetail } from '../../types';
import { tasksAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface TaskDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
  onTaskUpdated?: (task: Task) => void;
}

const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  isOpen,
  onClose,
  task,
  onTaskUpdated
}) => {
  const { user } = useAuth();
  const [feedbacks, setFeedbacks] = useState<TaskFeedback[]>([]);
  const [finishDetails, setFinishDetails] = useState<TaskFinishDetail[]>([]);
  const [newFeedback, setNewFeedback] = useState('');
  const [newFinishDetail, setNewFinishDetail] = useState({
    description: '',
    notes: '',
    completedAt: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [isSubmittingFinishDetail, setIsSubmittingFinishDetail] = useState(false);
  const [showAddFeedback, setShowAddFeedback] = useState(false);
  const [showAddFinishDetail, setShowAddFinishDetail] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState<TaskFeedback | null>(null);
  const [editFeedbackContent, setEditFeedbackContent] = useState('');
  const [isEditingEstimatedHours, setIsEditingEstimatedHours] = useState(false);
  const [estimatedHours, setEstimatedHours] = useState(0);

  const canAddFeedback = user && (user.role === 'ADMIN' || user.role === 'MANAGER');
  const canAddFinishDetail = task?.status === 'DONE';

  useEffect(() => {
    if (isOpen && task) {
      loadTaskDetails();
      setEstimatedHours(task.estimatedHours || 0);
      setNewFinishDetail(prev => ({
        ...prev,
        completedAt: new Date().toISOString().slice(0, 16)
      }));
    }
  }, [isOpen, task]);

  const loadTaskDetails = async () => {
    if (!task) return;
    
    try {
      setIsLoading(true);
      const [feedbackData, finishDetailData] = await Promise.all([
        tasksAPI.getFeedbacks(task.id),
        tasksAPI.getFinishDetails(task.id)
      ]);
      setFeedbacks(feedbackData);
      setFinishDetails(finishDetailData);
    } catch (error) {
      console.error('Error loading task details:', error);
      toast.error('Failed to load task details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!task || !user || !newFeedback.trim()) return;

    try {
      setIsSubmittingFeedback(true);
      const feedback = await tasksAPI.createFeedback(task.id, newFeedback.trim(), user.id);
      setFeedbacks([feedback, ...feedbacks]);
      setNewFeedback('');
      setShowAddFeedback(false);
      toast.success('Feedback added successfully');
    } catch (error) {
      console.error('Error adding feedback:', error);
      toast.error('Failed to add feedback');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleSubmitFinishDetail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!task || !user || !newFinishDetail.description.trim()) return;

    try {
      setIsSubmittingFinishDetail(true);
      const finishDetail = await tasksAPI.createFinishDetail(
        task.id,
        newFinishDetail.description.trim(),
        newFinishDetail.notes.trim() || undefined,
        user.id,
        newFinishDetail.completedAt
      );
      setFinishDetails([finishDetail, ...finishDetails]);
      setNewFinishDetail({
        description: '',
        notes: '',
        completedAt: new Date().toISOString().slice(0, 16)
      });
      setShowAddFinishDetail(false);
      toast.success('Finish detail added successfully');
    } catch (error) {
      console.error('Error adding finish detail:', error);
      toast.error('Failed to add finish detail');
    } finally {
      setIsSubmittingFinishDetail(false);
    }
  };

  const handleEditFeedback = (feedback: TaskFeedback) => {
    setEditingFeedback(feedback);
    setEditFeedbackContent(feedback.content);
  };

  const handleUpdateFeedback = async () => {
    if (!editingFeedback || !user || !editFeedbackContent.trim()) return;

    try {
      const updatedFeedback = await tasksAPI.updateFeedback(
        editingFeedback.id,
        editFeedbackContent.trim(),
        user.id
      );
      setFeedbacks(feedbacks.map(f => f.id === editingFeedback.id ? updatedFeedback : f));
      setEditingFeedback(null);
      setEditFeedbackContent('');
      toast.success('Feedback updated successfully');
    } catch (error) {
      console.error('Error updating feedback:', error);
      toast.error('Failed to update feedback');
    }
  };

  const handleDeleteFeedback = async (feedbackId: string) => {
    if (!user) return;

    if (!window.confirm('Are you sure you want to delete this feedback?')) return;

    try {
      await tasksAPI.deleteFeedback(feedbackId, user.id);
      setFeedbacks(feedbacks.filter(f => f.id !== feedbackId));
      toast.success('Feedback deleted successfully');
    } catch (error) {
      console.error('Error deleting feedback:', error);
      toast.error('Failed to delete feedback');
    }
  };

  const canEditFeedback = (feedback: TaskFeedback) => {
    return user && (feedback.userId === user.id || user.role === 'ADMIN');
  };

  const handleUpdateEstimatedHours = async () => {
    if (!task || !user) return;

    try {
      setIsLoading(true);
      const updatedTask = await tasksAPI.updateTaskTime(task.id, {
        estimatedHours: estimatedHours,
      });

      setIsEditingEstimatedHours(false);
      onTaskUpdated?.(updatedTask);
      toast.success('Estimated hours updated successfully');
    } catch (error) {
      console.error('Error updating estimated hours:', error);
      toast.error('Failed to update estimated hours');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH': return 'text-red-600 bg-red-100';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
      case 'LOW': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TODO': return 'text-gray-600 bg-gray-100';
      case 'IN_PROGRESS': return 'text-blue-600 bg-blue-100';
      case 'DONE': return 'text-green-600 bg-green-100';
      case 'CANCELLED': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (!isOpen || !task) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl z-10 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <FolderIcon className="w-6 h-6 text-blue-600" />
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{task.title}</h2>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task.status)}`}>
                      {task.status}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Task Details */}
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Task Information</h3>
                    
                    {task.description && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <p className="text-gray-900">{task.description}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      {task.startDate && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                          <div className="flex items-center space-x-2">
                            <CalendarIcon className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-900">{formatDate(task.startDate)}</span>
                          </div>
                        </div>
                      )}

                      {task.endDate && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                          <div className="flex items-center space-x-2">
                            <CalendarIcon className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-900">{formatDate(task.endDate)}</span>
                          </div>
                        </div>
                      )}

                      {task.dueDate && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                          <div className="flex items-center space-x-2">
                            <ClockIcon className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-900">{formatDate(task.dueDate)}</span>
                          </div>
                        </div>
                      )}

                      {/* Estimated Hours - Editable for Admins */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-gray-700">Estimated Hours</label>
                          {user?.role === 'ADMIN' && !isEditingEstimatedHours && (
                            <button
                              onClick={() => setIsEditingEstimatedHours(true)}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Edit Estimated Hours"
                            >
                              <PencilIcon className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        {isEditingEstimatedHours && user?.role === 'ADMIN' ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              value={estimatedHours}
                              onChange={(e) => setEstimatedHours(Number(e.target.value))}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <span className="text-sm text-gray-500">hours</span>
                            <button
                              onClick={handleUpdateEstimatedHours}
                              disabled={isLoading}
                              className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setIsEditingEstimatedHours(false);
                                setEstimatedHours(task.estimatedHours || 0);
                              }}
                              className="px-2 py-1 text-gray-600 hover:text-gray-800 transition-colors text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-900">
                            {task.estimatedHours ? `${task.estimatedHours}h` : 'Not set'}
                          </span>
                        )}
                      </div>

                      {/* Time Tracking Summary - Admin View */}
                      {user?.role === 'ADMIN' && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-gray-900">Time Tracking Overview</h4>
                            <span className="text-xs text-gray-500">
                              Last updated: {new Date(task.updatedAt).toLocaleString()}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Estimated Hours */}
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Estimated</span>
                                <ClockIcon className="w-4 h-4 text-blue-500" />
                              </div>
                              <p className="text-lg font-semibold text-blue-900 mt-1">
                                {task.estimatedHours || 0}h
                              </p>
                            </div>

                            {/* Actual Hours */}
                            <div className="bg-green-50 p-3 rounded-lg">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-green-600 uppercase tracking-wide">Actual</span>
                                <ClockIcon className="w-4 h-4 text-green-500" />
                              </div>
                              <p className="text-lg font-semibold text-green-900 mt-1">
                                {task.actualHours || 0}h
                              </p>
                            </div>

                            {/* Progress */}
                            <div className="bg-purple-50 p-3 rounded-lg">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-purple-600 uppercase tracking-wide">Progress</span>
                                <div className="w-4 h-4 rounded-full bg-purple-200 flex items-center justify-center">
                                  <div
                                    className="w-2 h-2 rounded-full bg-purple-500"
                                    style={{
                                      transform: `scale(${(task.progressPercentage || 0) / 100})`
                                    }}
                                  />
                                </div>
                              </div>
                              <p className="text-lg font-semibold text-purple-900 mt-1">
                                {task.progressPercentage || 0}%
                              </p>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="mt-4">
                            <div className="flex justify-between text-sm text-gray-600 mb-2">
                              <span>Task Progress</span>
                              <span>{task.progressPercentage || 0}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${task.progressPercentage || 0}%` }}
                              />
                            </div>
                          </div>

                          {/* Time Analysis */}
                          {task.estimatedHours && task.estimatedHours > 0 && (
                            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">Time Analysis</h5>
                              <div className="text-sm text-gray-600">
                                {task.actualHours && task.actualHours > 0 ? (
                                  <>
                                    {task.actualHours > task.estimatedHours ? (
                                      <div className="flex items-center space-x-2 text-red-600">
                                        <ExclamationTriangleIcon className="w-4 h-4" />
                                        <span>
                                          Over estimate by {(task.actualHours - task.estimatedHours).toFixed(1)} hours
                                          ({(((task.actualHours - task.estimatedHours) / task.estimatedHours) * 100).toFixed(1)}% over)
                                        </span>
                                      </div>
                                    ) : task.actualHours < task.estimatedHours ? (
                                      <div className="flex items-center space-x-2 text-green-600">
                                        <CheckCircleIcon className="w-4 h-4" />
                                        <span>
                                          Under estimate by {(task.estimatedHours - task.actualHours).toFixed(1)} hours
                                          ({(((task.estimatedHours - task.actualHours) / task.estimatedHours) * 100).toFixed(1)}% under)
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="flex items-center space-x-2 text-blue-600">
                                        <CheckCircleIcon className="w-4 h-4" />
                                        <span>Exactly on estimate</span>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-gray-500">No actual hours logged yet</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Project and Team Info */}
                    {(task.project || task.team) && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        {task.project && (
                          <div className="mb-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                            <span className="text-sm text-gray-900">{task.project.name}</span>
                          </div>
                        )}
                        {task.team && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
                            <span className="text-sm text-gray-900">{task.team.name}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Assignees */}
                  {task.assignees && task.assignees.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Assigned Users ({task.assignees.length})
                      </h3>
                      <div className="space-y-3">
                        {task.assignees.map((assignment) => (
                          <div key={assignment.id} className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                  {assignment.user.avatar ? (
                                    <img
                                      src={assignment.user.avatar}
                                      alt={`${assignment.user.firstName} ${assignment.user.lastName}`}
                                      className="w-8 h-8 rounded-full"
                                    />
                                  ) : (
                                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                                      <UserIcon className="w-4 h-4 text-gray-600" />
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {assignment.user.firstName} {assignment.user.lastName}
                                  </p>
                                  <p className="text-xs text-gray-500">@{assignment.user.username}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500">Assigned</p>
                                <p className="text-xs text-gray-600">
                                  {new Date(task.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>

                            {/* Individual Progress Indicator */}
                            {user?.role === 'ADMIN' && (
                              <div className="mt-3 pt-3 border-t border-gray-100">
                                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                  <span>Individual Contribution</span>
                                  <span>
                                    {task.assignees.length > 1
                                      ? `${(100 / task.assignees.length).toFixed(0)}% expected`
                                      : '100% responsible'
                                    }
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div
                                    className="bg-blue-500 h-1.5 rounded-full"
                                    style={{
                                      width: `${task.assignees.length > 1
                                        ? (task.progressPercentage || 0) / task.assignees.length
                                        : (task.progressPercentage || 0)
                                      }%`
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Team Collaboration Summary */}
                      {user?.role === 'ADMIN' && task.assignees.length > 1 && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <h4 className="text-sm font-medium text-blue-900 mb-2">Team Collaboration</h4>
                          <div className="text-sm text-blue-700">
                            <p>• {task.assignees.length} team members working together</p>
                            <p>• Expected individual contribution: ~{(100 / task.assignees.length).toFixed(0)}% each</p>
                            <p>• Current overall progress: {task.progressPercentage || 0}%</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Right Column - Feedback and Finish Details */}
                <div className="space-y-6">
                  {/* Feedback Section */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Feedback</h3>
                      {canAddFeedback && (
                        <button
                          onClick={() => setShowAddFeedback(!showAddFeedback)}
                          className="flex items-center space-x-2 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                        >
                          <PlusIcon className="w-4 h-4" />
                          <span>Add Feedback</span>
                        </button>
                      )}
                    </div>

                    {/* Add Feedback Form */}
                    {showAddFeedback && canAddFeedback && (
                      <form onSubmit={handleSubmitFeedback} className="mb-4 p-3 bg-white rounded border">
                        <textarea
                          value={newFeedback}
                          onChange={(e) => setNewFeedback(e.target.value)}
                          placeholder="Provide feedback for this task..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows={3}
                          required
                        />
                        <div className="flex justify-end space-x-2 mt-2">
                          <button
                            type="button"
                            onClick={() => setShowAddFeedback(false)}
                            className="px-3 py-1 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={isSubmittingFeedback || !newFeedback.trim()}
                            className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                          >
                            {isSubmittingFeedback ? 'Adding...' : 'Add'}
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Feedback List */}
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {feedbacks.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          <ChatBubbleLeftRightIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">No feedback yet</p>
                        </div>
                      ) : (
                        feedbacks.map((feedback) => (
                          <div key={feedback.id} className="bg-white rounded p-3 border">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-gray-900 text-sm">
                                  {feedback.user.firstName} {feedback.user.lastName}
                                </span>
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  feedback.user.role === 'ADMIN'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {feedback.user.role}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">
                                  {formatDate(feedback.createdAt)}
                                </span>
                                {canEditFeedback(feedback) && (
                                  <div className="flex items-center space-x-1">
                                    <button
                                      onClick={() => handleEditFeedback(feedback)}
                                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                      title="Edit Feedback"
                                    >
                                      <PencilIcon className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteFeedback(feedback.id)}
                                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                      title="Delete Feedback"
                                    >
                                      <TrashIcon className="w-3 h-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            {editingFeedback?.id === feedback.id ? (
                              <div className="space-y-2">
                                <textarea
                                  value={editFeedbackContent}
                                  onChange={(e) => setEditFeedbackContent(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                  rows={2}
                                />
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={() => {
                                      setEditingFeedback(null);
                                      setEditFeedbackContent('');
                                    }}
                                    className="px-2 py-1 text-gray-600 hover:text-gray-800 transition-colors text-xs"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleUpdateFeedback}
                                    disabled={!editFeedbackContent.trim()}
                                    className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs"
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-gray-700 text-sm">{feedback.content}</p>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Finish Details Section */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Completion Details</h3>
                      {canAddFinishDetail && (
                        <button
                          onClick={() => setShowAddFinishDetail(!showAddFinishDetail)}
                          className="flex items-center space-x-2 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                        >
                          <PlusIcon className="w-4 h-4" />
                          <span>Add Detail</span>
                        </button>
                      )}
                    </div>

                    {/* Add Finish Detail Form */}
                    {showAddFinishDetail && canAddFinishDetail && (
                      <form onSubmit={handleSubmitFinishDetail} className="mb-4 p-3 bg-white rounded border">
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                            <textarea
                              value={newFinishDetail.description}
                              onChange={(e) => setNewFinishDetail(prev => ({ ...prev, description: e.target.value }))}
                              placeholder="Describe what was completed..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              rows={2}
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                            <textarea
                              value={newFinishDetail.notes}
                              onChange={(e) => setNewFinishDetail(prev => ({ ...prev, notes: e.target.value }))}
                              placeholder="Additional notes..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              rows={2}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Completion Time</label>
                            <input
                              type="datetime-local"
                              value={newFinishDetail.completedAt}
                              onChange={(e) => setNewFinishDetail(prev => ({ ...prev, completedAt: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2 mt-3">
                          <button
                            type="button"
                            onClick={() => setShowAddFinishDetail(false)}
                            className="px-3 py-1 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={isSubmittingFinishDetail || !newFinishDetail.description.trim()}
                            className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                          >
                            {isSubmittingFinishDetail ? 'Adding...' : 'Add Detail'}
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Finish Details List */}
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {finishDetails.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          <CheckCircleIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">No completion details yet</p>
                        </div>
                      ) : (
                        finishDetails.map((detail) => (
                          <div key={detail.id} className="bg-white rounded p-3 border">
                            <div className="flex items-start justify-between mb-2">
                              <span className="font-medium text-gray-900 text-sm">
                                {detail.user.firstName} {detail.user.lastName}
                              </span>
                              <span className="text-xs text-gray-500">
                                Completed: {formatDate(detail.completedAt)}
                              </span>
                            </div>
                            <p className="text-gray-700 text-sm mb-1">{detail.description}</p>
                            {detail.notes && (
                              <p className="text-gray-600 text-xs italic">Notes: {detail.notes}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              Added: {formatDate(detail.createdAt)}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default TaskDetailsModal;
